import { Button, Input, Select, Textarea } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ArticlePresente, CorpsArticle } from '../../api/presse.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import { RechercheReference, type ReferenceAgenda } from '../agenda/RechercheReference.js';
import styles from './PressePage.module.css';
export function FormArticle({
  galerieSlug,
  initial,
  enregistrer,
  attente,
}: {
  galerieSlug: string;
  initial?: ArticlePresente;
  enregistrer: (c: Partial<CorpsArticle>) => Promise<void>;
  attente: boolean;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [corps, setCorps] = useState<CorpsArticle>(() => ({
    genre: initial?.genre ?? 'article',
    titre: initial?.titre ?? '',
    support: initial?.support ?? '',
    auteur: initial?.auteur ?? '',
    paruLe: initial?.paruLe ?? null,
    url: initial?.url ?? '',
    langue: initial?.langue ?? (i18n.language === 'en' ? 'en' : 'fr'),
    extrait: initial?.extrait ?? '',
    isbn: initial?.isbn ?? '',
    pages: initial?.pages ?? null,
    expositions: initial?.expositions ?? [],
    artistes: initial?.artistes ?? [],
    oeuvres: initial?.oeuvres ?? [],
  }));
  const [erreur, setErreur] = useState<string | null>(null);
  const changer = <K extends keyof CorpsArticle>(k: K, v: CorpsArticle[K]) =>
    setCorps((c) => ({ ...c, [k]: v }));
  const champ = (k: 'titre' | 'support' | 'auteur' | 'url' | 'isbn', type = 'text') => (
    <Field htmlFor={`presse-${k}`} label={t(`regie:presse.champs.${k}`)}>
      <Input
        id={`presse-${k}`}
        type={type}
        required={k === 'titre'}
        maxLength={k === 'titre' ? 300 : undefined}
        value={corps[k]}
        onChange={(e) => changer(k, e.target.value)}
      />
    </Field>
  );
  const valeurs: ReferenceAgenda[] = [
    ...corps.expositions.map((id) => ({ type: 'exposition' as const, id })),
    ...corps.artistes.map((id) => ({ type: 'artiste' as const, id })),
    ...corps.oeuvres.map((id) => ({ type: 'oeuvre' as const, id })),
  ];
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        setErreur(null);
        const changements = initial
          ? Object.fromEntries(
              Object.entries(corps).filter(
                ([k, v]) => JSON.stringify(v) !== JSON.stringify(initial[k as keyof CorpsArticle]),
              ),
            )
          : corps;
        void enregistrer(changements).catch((err: unknown) =>
          setErreur(estApiError(err) ? err.message : t('commun:erreurReseau')),
        );
      }}
    >
      <Field htmlFor="presse-genre" label={t('regie:presse.champs.genre')}>
        <Select
          id="presse-genre"
          value={corps.genre}
          onValueChange={(v) => changer('genre', v as CorpsArticle['genre'])}
          options={['article', 'catalogue'].map((v) => ({
            value: v,
            label: t(`regie:presse.genres.${v}`),
          }))}
        />
      </Field>
      {champ('titre')}
      {champ('support')}
      {champ('auteur')}
      <Field htmlFor="presse-paruLe" label={t('regie:presse.champs.paruLe')}>
        <Input
          id="presse-paruLe"
          type="date"
          value={corps.paruLe ?? ''}
          onChange={(e) => changer('paruLe', e.target.value || null)}
        />
      </Field>
      {champ('url', 'url')}
      <Field htmlFor="presse-langue" label={t('regie:presse.champs.langue')}>
        <Select
          id="presse-langue"
          value={corps.langue}
          onValueChange={(v) => changer('langue', v as CorpsArticle['langue'])}
          options={[
            { value: 'fr', label: 'Français' },
            { value: 'en', label: 'English' },
          ]}
        />
      </Field>
      <Field htmlFor="presse-extrait" label={t('regie:presse.champs.extrait')}>
        <Textarea
          id="presse-extrait"
          maxLength={5000}
          value={corps.extrait}
          onChange={(e) => changer('extrait', e.target.value)}
        />
      </Field>
      {corps.genre === 'catalogue' ? (
        <>
          {champ('isbn')}
          <Field htmlFor="presse-pages" label={t('regie:presse.champs.pages')}>
            <Input
              id="presse-pages"
              type="number"
              min={1}
              max={10000}
              value={corps.pages ?? ''}
              onChange={(e) => changer('pages', e.target.value ? Number(e.target.value) : null)}
            />
          </Field>
        </>
      ) : null}
      <RechercheReference
        galerieSlug={galerieSlug}
        types={['exposition', 'artiste', 'oeuvre']}
        valeurs={valeurs}
        onChanger={(v) =>
          setCorps((c) => ({
            ...c,
            expositions: v.filter((r) => r.type === 'exposition').map((r) => r.id),
            artistes: v.filter((r) => r.type === 'artiste').map((r) => r.id),
            oeuvres: v.filter((r) => r.type === 'oeuvre').map((r) => r.id),
          }))
        }
      />
      {erreur ? <p role="alert">{erreur}</p> : null}
      <Button type="submit" disabled={attente}>
        {t('regie:presse.enregistrer')}
      </Button>
    </form>
  );
}
